import React, { useEffect, useMemo, useState } from "react";
import { Download, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { QuotePreview } from "../QuotePreview";
import { cloneBim4DPricingRules } from "../../lib/bim4dPricingDefaults";
import { getApiBase } from "../../lib/apiBase";

const STORAGE_KEY = "bimcubes_estimator_4d_page_v2";

const defaultState = {
  projectType: "Neuf",
  area: 2000,
  name: "",
  company: "",
  email: "",
  phone: "",
  clientAddress: "",
  d4: {
    tasks: 50,
    videoExport: false,
    navisworksSynchro: false,
    siteFollowUp: false,
    followUpWeeks: 4,
    followUpIntensity: "Standard",
  },
};

const getUnitRate = (jh, rateTiers) => {
  const value = Number(jh) || 0;
  for (const tier of rateTiers || []) {
    if (value >= tier.min) return tier.rate;
  }
  return 280;
};

export const Bim4DEstimatorSection = () => {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : defaultState;
  });
  const [pricingRules, setPricingRules] = useState(() => cloneBim4DPricingRules());
  const [openPreview, setOpenPreview] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    let active = true;
    const API = getApiBase();
    fetch(`${API}/api/bim-4d/pricing-rules`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setPricingRules(data);
      })
      .catch(() => {
        if (active) setPricingRules(cloneBim4DPricingRules());
      });
    return () => {
      active = false;
    };
  }, []);

  const surfaceRetained = useMemo(
    () => Math.max(state.area || 0, pricingRules.surfaceSettings?.minimumSurface ?? 1000),
    [state.area, pricingRules]
  );

  const tasksRetained = useMemo(
    () => Math.max(state.d4?.tasks || 0, pricingRules.taskSettings?.minimumTasks ?? 20),
    [state.d4?.tasks, pricingRules]
  );

  const baseSurfaceDays = useMemo(
    () => surfaceRetained * (pricingRules.surfaceSettings?.jhPerSquareMeter ?? 0.003),
    [surfaceRetained, pricingRules]
  );

  const baseTaskDays = useMemo(
    () => tasksRetained * (pricingRules.taskSettings?.jhPerTask ?? 0.3),
    [tasksRetained, pricingRules]
  );

  const baseDays = useMemo(() => baseSurfaceDays + baseTaskDays, [baseSurfaceDays, baseTaskDays]);

  const deliverablesFactor = useMemo(() => {
    const videoFactor = state.d4?.videoExport ? pricingRules.deliverables?.videoExportFactor ?? 1.2 : 1;
    const navisworksFactor = state.d4?.navisworksSynchro ? pricingRules.deliverables?.navisworksSynchroFactor ?? 1.8 : 1;
    return (pricingRules.deliverables?.defaultPackageFactor ?? 1) * videoFactor * navisworksFactor;
  }, [state.d4, pricingRules]);

  const afterDeliverablesDays = useMemo(() => baseDays * deliverablesFactor, [baseDays, deliverablesFactor]);

  const followUpWeeks = useMemo(
    () => Math.max(state.d4?.followUpWeeks || 0, pricingRules.siteFollowUp?.minimumWeeks ?? 4),
    [state.d4?.followUpWeeks, pricingRules]
  );

  const followUpIntensityPerWeek = useMemo(() => {
    const map = {
      Léger: pricingRules.siteFollowUp?.weeklyLight ?? 2,
      Standard: pricingRules.siteFollowUp?.weeklyStandard ?? 4,
      Renforcé: pricingRules.siteFollowUp?.weeklyIntensive ?? 6,
    };
    return map[state.d4?.followUpIntensity || "Standard"];
  }, [state.d4?.followUpIntensity, pricingRules]);

  const sizeFactor = useMemo(() => {
    const threshold = pricingRules.siteFollowUp?.sizeFactorThreshold ?? 40;
    const min = pricingRules.siteFollowUp?.sizeFactorMin ?? 1;
    const max = pricingRules.siteFollowUp?.sizeFactorMax ?? 2;
    return Math.min(max, Math.max(min, afterDeliverablesDays / threshold));
  }, [pricingRules, afterDeliverablesDays]);

  const followUpDays = useMemo(() => {
    if (!state.d4?.siteFollowUp) return 0;
    return followUpWeeks * followUpIntensityPerWeek * sizeFactor;
  }, [state.d4?.siteFollowUp, followUpWeeks, followUpIntensityPerWeek, sizeFactor]);

  const days = useMemo(() => Math.round((afterDeliverablesDays + followUpDays) * 100) / 100, [afterDeliverablesDays, followUpDays]);
  const unitRate = useMemo(() => getUnitRate(days, pricingRules.rateTiers || []), [days, pricingRules]);
  const price = useMemo(() => Math.round(days * unitRate), [days, unitRate]);

  const calcDetail = useMemo(
    () => ({
      mode: "4D",
      formulaVersion: "surface-tasks-deliverables-followup",
      breakdown: {
        surfaceRetained,
        tasksRetained,
        baseSurfaceDays,
        baseTaskDays,
        baseDays,
        deliverablesFactor,
        afterDeliverablesDays,
        siteFollowUpEnabled: !!state.d4?.siteFollowUp,
        followUpWeeks,
        followUpIntensity: state.d4?.followUpIntensity || "Standard",
        followUpIntensityPerWeek,
        sizeFactor,
        followUpDays,
      },
      unit_rate_applied: unitRate,
      total_days: days,
    }),
    [surfaceRetained, tasksRetained, baseSurfaceDays, baseTaskDays, baseDays, deliverablesFactor, afterDeliverablesDays, state.d4, followUpWeeks, followUpIntensityPerWeek, sizeFactor, followUpDays, unitRate, days]
  );

  const helperText = {
    surface: `Surface minimale retenue : ${(pricingRules.surfaceSettings?.minimumSurface ?? 1000).toLocaleString("fr-FR")} m².`,
    tasks: `Minimum retenu : ${pricingRules.taskSettings?.minimumTasks ?? 20} tâches.`,
    package: "Livrable de base inclus par défaut.",
    video: "Ajoute du temps de production pour générer des exports visuels de la simulation.",
    navisworks: "Ajoute du temps pour structurer la simulation dans un environnement 4D dédié.",
    followUp: "Ajoute un accompagnement périodique pour mettre à jour la maquette, réévaluer le planning et suivre l’évolution du chantier.",
  };

  return (
    <TooltipProvider>
      <section className="px-4 pb-16 pt-8 sm:px-6 lg:px-8 lg:pb-24" data-testid="bim4d-calculator-section" id="calculateur">
        <div className="mx-auto max-w-[1520px] rounded-[2.9rem] border border-[#d9e7d5]/85 bg-[linear-gradient(180deg,_rgba(255,255,255,0.78)_0%,_rgba(248,252,243,0.98)_100%)] px-8 py-10 shadow-[0_36px_96px_-58px_rgba(15,118,110,0.24)] sm:px-11 sm:py-12 lg:px-14 lg:py-14">
          <div className="max-w-4xl">
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="bim4d-calculator-eyebrow">Calculateur BIM 4D</div>
            <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="bim4d-calculator-title">Estimer un projet BIM 4D</h2>
            <p className="mt-5 text-lg leading-9 text-slate-600" data-testid="bim4d-calculator-subtitle">Renseignez les paramètres principaux de votre planning et de votre maquette pour obtenir une première estimation indicative.</p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/90 shadow-[0_28px_76px_-50px_rgba(15,118,110,0.2)]" data-testid="bim4d-calculator-form-card">
              <CardHeader><CardTitle className="text-slate-900">Paramètres du projet</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <Label htmlFor="bim4d-area-input">Surface du projet (m²)</Label>
                    <div className="mt-4 space-y-3">
                      <Slider data-testid="bim4d-area-slider" max={80000} min={0} onValueChange={(v) => setState({ ...state, area: v[0] })} step={100} value={[state.area]} />
                      <div className="flex items-center gap-3"><Input data-testid="bim4d-area-input" id="bim4d-area-input" min={0} onChange={(e) => setState({ ...state, area: Math.max(0, Number(e.target.value) || 0) })} type="number" value={state.area} /><span className="text-sm text-slate-600">m²</span></div>
                    </div>
                    <p className="mt-2 text-xs text-slate-500" data-testid="bim4d-area-help">{helperText.surface}</p>
                  </div>
                  <div>
                    <Label htmlFor="bim4d-tasks-input">Nombre de tâches à relier</Label>
                    <div className="mt-4 space-y-3">
                      <Slider data-testid="bim4d-tasks-slider" max={400} min={0} onValueChange={(v) => setState({ ...state, d4: { ...(state.d4 || {}), tasks: v[0] } })} step={1} value={[state.d4?.tasks || 0]} />
                      <div className="flex items-center gap-3"><Input data-testid="bim4d-tasks-input" id="bim4d-tasks-input" min={0} onChange={(e) => setState({ ...state, d4: { ...(state.d4 || {}), tasks: Math.max(0, Number(e.target.value) || 0) } })} type="number" value={state.d4?.tasks || 0} /><span className="text-sm text-slate-600">tâches</span></div>
                    </div>
                    <p className="mt-2 text-xs text-slate-500" data-testid="bim4d-tasks-help">{helperText.tasks}</p>
                  </div>

                  <div className="md:col-span-2 grid gap-3">
                    <Label>Livrables</Label>
                    <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Maquette + Planning + PDF</div>
                        <div className="text-xs text-slate-600">Inclus par défaut</div>
                      </div>
                      <Switch checked data-testid="bim4d-default-package-switch" disabled />
                    </div>
                    <p className="text-xs text-slate-500" data-testid="bim4d-default-package-help">{helperText.package}</p>

                    <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Exports vidéo</div>
                        <div className="text-xs text-slate-600">Option ON/OFF</div>
                      </div>
                      <Switch checked={!!state.d4?.videoExport} data-testid="bim4d-video-export-switch" onCheckedChange={(v) => setState({ ...state, d4: { ...(state.d4 || {}), videoExport: v } })} />
                    </div>
                    <p className="text-xs text-slate-500" data-testid="bim4d-video-help">{helperText.video}</p>

                    <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Navisworks ou Synchro</div>
                        <div className="text-xs text-slate-600">Option ON/OFF</div>
                      </div>
                      <Switch checked={!!state.d4?.navisworksSynchro} data-testid="bim4d-navisworks-switch" onCheckedChange={(v) => setState({ ...state, d4: { ...(state.d4 || {}), navisworksSynchro: v } })} />
                    </div>
                    <p className="text-xs text-slate-500" data-testid="bim4d-navisworks-help">{helperText.navisworks}</p>
                  </div>

                  <div className="md:col-span-2 grid gap-3">
                    <Label>Suivi de chantier</Label>
                    <div className="flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-900">Suivi de chantier</div>
                        <div className="text-xs text-slate-600">Active un accompagnement périodique</div>
                      </div>
                      <Switch checked={!!state.d4?.siteFollowUp} data-testid="bim4d-site-follow-up-switch" onCheckedChange={(v) => setState({ ...state, d4: { ...(state.d4 || {}), siteFollowUp: v } })} />
                    </div>
                    <p className="text-xs text-slate-500" data-testid="bim4d-follow-up-help">{helperText.followUp}</p>

                    {state.d4?.siteFollowUp ? (
                      <>
                        <div>
                          <Label htmlFor="bim4d-follow-up-weeks-input">Nombre de semaines de suivi</Label>
                          <Input className="mt-3 bg-white" data-testid="bim4d-follow-up-weeks-input" id="bim4d-follow-up-weeks-input" min={pricingRules.siteFollowUp?.minimumWeeks ?? 4} onChange={(e) => setState({ ...state, d4: { ...(state.d4 || {}), followUpWeeks: Math.max(0, Number(e.target.value) || 0) } })} type="number" value={state.d4?.followUpWeeks || 0} />
                          <p className="mt-2 text-xs text-slate-500" data-testid="bim4d-follow-up-weeks-help">Minimum retenu : {pricingRules.siteFollowUp?.minimumWeeks ?? 4} semaines.</p>
                        </div>
                        <div>
                          <Label>Intensité du suivi</Label>
                          <RadioGroup className="mt-3 grid gap-3 md:grid-cols-3" onValueChange={(value) => setState({ ...state, d4: { ...(state.d4 || {}), followUpIntensity: value } })} value={state.d4?.followUpIntensity || "Standard"}>
                            {[
                              { label: "Léger", copy: `${pricingRules.siteFollowUp?.weeklyLight ?? 2} JH/semaine — suivi ponctuel` },
                              { label: "Standard", copy: `${pricingRules.siteFollowUp?.weeklyStandard ?? 4} JH/semaine — suivi régulier` },
                              { label: "Renforcé", copy: `${pricingRules.siteFollowUp?.weeklyIntensive ?? 6} JH/semaine — suivi plus intensif` },
                            ].map((item) => (
                              <Label className="rounded-xl border border-[#d9e7d5] bg-white p-4 text-sm cursor-pointer" data-testid={`bim4d-intensity-${item.label.toLowerCase()}-option`} key={item.label}>
                                <div className="flex items-center gap-2"><RadioGroupItem value={item.label} /> <span className="font-medium text-slate-900">{item.label}</span></div>
                                <div className="mt-2 text-xs leading-6 text-slate-500">{item.copy}</div>
                              </Label>
                            ))}
                          </RadioGroup>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_28px_76px_-50px_rgba(15,118,110,0.2)]" data-testid="bim4d-estimate-summary-card">
              <CardHeader><CardTitle className="text-slate-900">Estimation indicative</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div className="text-4xl font-semibold text-[#0f766e]" data-testid="bim4d-total-days-value">{days.toFixed(2)} j‑h</div>
                <p className="text-base text-slate-600" data-testid="bim4d-total-price-value">Équivalent : {price.toLocaleString("fr-FR")} € HT</p>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>Surface retenue : {surfaceRetained.toLocaleString("fr-FR")} m²</p>
                  <p>Tâches retenues : {tasksRetained}</p>
                  <p>Facteur livrables : × {deliverablesFactor.toFixed(2)}</p>
                  <p>Suivi chantier : {state.d4?.siteFollowUp ? `${followUpWeeks} semaines • ${state.d4?.followUpIntensity}` : "Non activé"}</p>
                </div>
                <div className="flex flex-col gap-3">
                  <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="bim4d-preview-quote-button" onClick={() => setOpenPreview(true)}>Aperçu du devis complet</Button>
                  <Button data-testid="bim4d-download-estimate-button" onClick={() => {
                    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Estimation Bimcubes 4D</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:32px;color:#0f172a}h1{color:#0f766e}table{border-collapse:collapse;width:100%}td,th{border:1px solid #e2e8f0;padding:8px}th{background:#f8fafc;text-align:left}.badge{display:inline-block;padding:2px 8px;border-radius:9999px;background:#ecfdf5;color:#0f766e;font-weight:700;font-size:12px}</style></head><body><h1>Estimation indicative BIM 4D – Bimcubes <span class="badge">ESTIMATION</span></h1><table><tbody><tr><td>Total (jours‑homme)</td><td>${days.toFixed(2)} j‑h</td></tr><tr><td>Équivalent montant</td><td>${price.toLocaleString("fr-FR")} € HT</td></tr></tbody></table></body></html>`;
                    const blob = new Blob([html], { type: "text/html" });
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = `estimation-bim4d-bimcubes-${Date.now()}.html`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                    toast.success("Estimation téléchargée (HTML)");
                  }} variant="outline"><Download className="mr-2 h-4 w-4" /> Télécharger estimation (HTML)</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <QuotePreview adminDetail={calcDetail} data={{ state, price, days }} loiPhases={[]} mode="4D" onOpenChange={setOpenPreview} open={openPreview} unitRate={unitRate} />
        </div>
      </section>
    </TooltipProvider>
  );
};